
import React from 'react';
import { GraduationCap, BookOpen, ExternalLink, Lightbulb } from 'lucide-react';
import { SkillsGapResult } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { BaseModal } from './BaseModal';

interface Props {
  careerTitle: string;
  data: SkillsGapResult;
  onClose: () => void;
}

export const SkillsGapModal: React.FC<Props> = ({ careerTitle, data, onClose }) => {
  const { theme } = useTheme();
  const fStyle = theme.colors.features.bridger;

  // Parse grounding chunks to find Search data
  const resources = data.chunks
    .map((c: any) => {
        const webData = c.web;
        if (!webData) return null;
        
        return {
            title: webData.title || "Resource",
            uri: webData.uri,
            source: new URL(webData.uri).hostname.replace('www.', '')
        };
    })
    .filter((res) => res && res.uri);

  const uniqueResources = resources.filter((res, index, self) =>
    index === self.findIndex((r) => r?.uri === res?.uri)
  );

  return (
    <BaseModal
        onClose={onClose}
        title={careerTitle}
        icon={<GraduationCap size={16} />}
        headerColor={fStyle}
        maxWidth="max-w-4xl"
        footer={
             <div className={`text-center text-xs ${theme.colors.text.secondary}`}>
                 Powered by Google Search Grounding
            </div>
        }
    >
        <div className="flex flex-col md:flex-row h-full">
            {/* AI Analysis Text */}
            <div className="flex-1 p-6 md:p-8 space-y-4">
                <h3 className={`text-lg font-bold flex items-center gap-2 ${theme.colors.text.main}`}>
                    <Lightbulb size={18} className={fStyle.icon} />
                    Gap Analysis & Strategy
                </h3>
                <div className={`prose prose-sm max-w-none leading-relaxed whitespace-pre-line ${theme.colors.text.secondary}`}>
                    <p>{data.text}</p>
                </div>
            </div>

            {/* Resources Sidebar */}
            <div className={`w-full md:w-80 border-l p-6 shrink-0 ${theme.mode === 'dark' ? 'bg-slate-850 border-slate-700' : 'bg-slate-50 border-gray-100'}`}>
                <h4 className={`text-xs font-bold uppercase tracking-wider mb-4 ${theme.colors.text.secondary}`}>
                    Recommended Resources
                </h4>
                
                {uniqueResources.length > 0 ? (
                    <div className="space-y-3">
                        {uniqueResources.map((res, idx) => (
                            <a 
                                key={idx}
                                href={res!.uri}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`block p-3 rounded-xl border shadow-sm transition-all group ${
                                    theme.mode === 'dark' 
                                    ? 'bg-slate-800 border-slate-700 hover:border-emerald-500' 
                                    : 'bg-white border-gray-200 hover:border-emerald-300 hover:shadow-md'
                                }`}
                            >
                                <div className="flex justify-between items-start">
                                    <div className="flex items-start gap-2">
                                        <BookOpen size={16} className={`mt-0.5 shrink-0 ${fStyle.icon}`} />
                                        <div>
                                            <div className={`font-bold text-sm transition-colors line-clamp-2 ${theme.colors.text.main} group-hover:text-emerald-500`}>
                                                {res!.title}
                                            </div>
                                            <div className={`text-xs mt-1 ${theme.colors.text.secondary}`}>
                                                {res!.source}
                                            </div>
                                        </div>
                                    </div>
                                    <ExternalLink size={12} className={`${theme.colors.text.secondary} group-hover:text-emerald-500 shrink-0`} />
                                </div>
                            </a>
                        ))}
                    </div>
                ) : (
                    <div className={`text-center py-8 text-sm italic ${theme.colors.text.secondary}`}>
                        No direct links found, but check the analysis for specific course names.
                    </div>
                )}
            </div>
        </div>
    </BaseModal>
  );
};
